import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  DATA_CONTROLLER_CONTACT_EMAIL,
  DATA_CONTROLLER_NAME,
  LEGAL_DOCUMENT_VERSION,
  MIN_USER_AGE,
} from '@/config/legal';

/**
 * Условия использования.
 *
 * Как и Privacy Policy, живёт вне AppShell (см. router.tsx) — правовой
 * документ не должен зависеть от статуса backend.
 */
export function TermsOfUsePage() {
  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-6 py-10 text-sm text-text-2">
      <div>
        <Link to="/" className="text-xs text-accent hover:underline">
          ← На главную
        </Link>
        <h1 className="mt-3 font-display text-h1 text-text">Условия использования</h1>
        <p className="mt-1 text-xs text-text-3">
          Редакция от {LEGAL_DOCUMENT_VERSION}. Использование сервиса «Астра студия» (АСТРА space | Студия) означает
          согласие с этими условиями.
        </p>
      </div>

      <Section title="1. Что такое сервис">
        <p>
          «Астра студия» — сервис готовых визуальных перевоплощений через тематические
          коллекции: пользователь выбирает результат визуальными параметрами, а не пишет
          технический запрос к нейросети. У сервиса нет режима свободного ввода текстового
          prompt.
        </p>
        <p className="mt-2">
          Сервис предназначен для пользователей, достигших {MIN_USER_AGE} лет. Начиная работу,
          пользователь подтверждает, что ему есть {MIN_USER_AGE} лет.
        </p>
      </Section>

      <Section title="2. Право на загружаемые фотографии">
        <p>
          Загружая фотографию, пользователь подтверждает, что вправе использовать её для создания
          портрета через сервис — в том числе если на ней есть другие люди или животные.
          Ответственность за то, что такое право действительно есть, несёт пользователь,
          загрузивший фотографию.
        </p>
        <p className="mt-2">
          Сервис вправе отказать в обработке фотографии, если она нарушает эти условия, содержит
          материалы, запрещённые к обработке (в частности, сексуализированные изображения
          несовершеннолетних, материалы, разжигающие ненависть или насилие), или не проходит
          автоматическую проверку перед генерацией.
        </p>
      </Section>

      <Section title="3. Что сервис не обещает">
        <p>
          Сервис создаёт художественную интерпретацию фотографии в выбранной эстетике, а не её
          техническую копию. Абсолютное совпадение лица с оригиналом и идеальная согласованность
          черт между несколькими кадрами одной фотосессии не гарантируются — качество и сходство
          результата зависят от используемой модели генерации.
        </p>
      </Section>

      <Section title="4. Бесплатные генерации, кредиты, вознаграждения">
        <p>
          Каждому пользователю при первом входе начисляется ограниченное число бесплатных
          генераций. Дополнительные генерации можно получить, приглашая других пользователей по
          реферальной ссылке (награда начисляется только после того, как приглашённый создаст свой
          первый портрет) или, где это включено, просмотром рекламы. Кредиты не являются денежным
          средством, не обмениваются на деньги и не могут быть переданы другому пользователю
          напрямую.
        </p>
        <p className="mt-2">
          Точные размеры лимитов и вознаграждений могут изменяться и приведены в интерфейсе
          сервиса на момент использования.
        </p>
      </Section>

      <Section title="5. Ограничение ответственности">
        <p>
          Сервис предоставляется «как есть». Оператор не гарантирует бесперебойную работу сервиса,
          доступность конкретного AI-провайдера или сохранность результатов дольше срока,
          указанного в Политике обработки персональных данных, и не несёт ответственности за
          содержание фотографий, загруженных пользователями.
        </p>
      </Section>

      <Section title="6. Удаление аккаунта">
        <p>
          Пользователь может удалить свой аккаунт в любой момент в настройках сервиса. Удаление
          необратимо и стирает баланс, историю генераций и фотосессий, избранное, реферальные
          связи и связанные аккаунты Telegram/VK. Подробности — в Политике обработки персональных
          данных.
        </p>
      </Section>

      <Section title="7. Применимое право и контакты">
        <p>
          Эти условия и связанные с ними отношения регулируются законодательством Российской
          Федерации. Оператор сервиса — {DATA_CONTROLLER_NAME}. Обращения — на адрес{' '}
          <a href={`mailto:${DATA_CONTROLLER_CONTACT_EMAIL}`} className="text-accent hover:underline">
            {DATA_CONTROLLER_CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <p className="text-xs text-text-3">
        См. также{' '}
        <Link to="/privacy" className="text-accent hover:underline">
          Политику обработки персональных данных
        </Link>
        .
      </p>
    </main>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-semibold text-text">{title}</h2>
      <div className="mt-2 leading-relaxed">{children}</div>
    </section>
  );
}
